import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, GoldButton, OutlineButton } from "@/components/Primitives";
import { PLANS } from "@/config/branding";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { Check, CreditCard, Calendar, BadgeCheck, ArrowRight } from "lucide-react";

export default function Subscription() {
  const { user, entitlements } = useAuth();
  const navigate = useNavigate();
  const currentTier = user?.tier || "personal";
  const current = PLANS.find((p) => p.key === currentTier);
  const planName = entitlements?.tier_name || (current ? current.name : "Personal Finance");

  const Info = ({ icon: Icon, label, value, tone = "#C6A45C" }) => (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center shrink-0">
        <Icon size={18} style={{ color: tone }} strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A7F70]">{label}</div>
        <div className="text-[#2C2A28]">{value}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="subscription-page">
      <PageHeader title="Subscription" subtitle="Manage your Finetomi plan and billing." />

      <Card className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] gold-text mb-1">Current plan</div>
            <div className="font-display text-3xl font-light text-[#2C2A28]" data-testid="current-plan-name">{planName}</div>
            {current && <div className="text-[#8A7F70] text-sm mt-1">{current.tagline}</div>}
          </div>
          <div className="text-right">
            <div className="font-display text-4xl gold-text">{current ? `$${current.price}` : "—"}<span className="text-sm text-[#A99F90]">/mo</span></div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-7 pt-6 border-t border-[#EAE0D2]">
          <Info icon={BadgeCheck} label="Billing status" value="Beta — no charge yet" tone="#8FB996" />
          <Info icon={Calendar} label="Next renewal" value="—" />
          <Info icon={CreditCard} label="Payment method" value="Added when Stripe goes live" />
        </div>
        <div className="flex flex-wrap gap-3 mt-7">
          <GoldButton data-testid="upgrade-plan-btn" onClick={() => navigate("/")}>Upgrade plan <ArrowRight size={15} /></GoldButton>
          <OutlineButton data-testid="cancel-subscription-btn" disabled title="Available when billing goes live"
            className="opacity-60 cursor-not-allowed">Cancel subscription</OutlineButton>
        </div>
        <p className="text-xs text-[#A99F90] mt-3">Secure billing via Stripe is coming soon. During beta your plan is active with no charge.</p>
      </Card>

      <h2 className="font-display text-2xl font-light mb-4 text-[#2C2A28]">All plans</h2>
      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((p) => {
          const isCurrent = p.key === currentTier;
          return (
            <Card key={p.key} data-testid={`sub-plan-${p.key}`} className={isCurrent ? "border-[#C6A45C] border-2" : ""}>
              <div className="flex items-center justify-between">
                <div className="font-display text-xl text-[#2C2A28]">{p.name}</div>
                {isCurrent && <span className="text-[10px] uppercase tracking-wider bg-[#C6A45C]/15 text-[#8a6d2f] rounded-full px-2.5 py-1">Current</span>}
              </div>
              <div className="mt-3 font-display text-3xl font-light gold-text">${p.price}<span className="text-xs text-[#A99F90]">/mo</span></div>
              <ul className="mt-5 space-y-2.5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#6b6256]"><Check size={15} className="text-[#8FB996] shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>

      <DisclaimerFooter className="mt-8 border-t border-[#EAE0D2] pt-5" />
    </div>
  );
}
